// @flow
import React, { useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

//smooth-scroll
import { scroller } from 'react-scroll';
// Selectors
import * as selectors from '../selectors';
// Components
import { Button, ConfirmationModal } from '../../common';
import ProductSearch from './ProductSearch';
import BlendTemplateItem from '../containers/BlendTemplateItem';
import FatLevelSelector from '../../current-order/components/FatLevelSelector';
// Images
import BlendBox from '../images/blend_box.png';
import EmptyCart from '../../current-order/images/empty-cart.png';
// Helpers
import {
  InputFieldErrorMessage,
  validationHelpers,
  priceConverter,
} from '../../common';
// Analytics
import * as Analytics from '../../common/analytics';
// Styles
import './BlendCalculatorPage.css';
// Types
import type {
  BlendTemplate,
  BlendTemplateItem as BlendTemplateItemType,
} from '../types';

type Props = {
  blendTemplate: BlendTemplate,
  blendTemplateItems: BlendTemplateItemType,
  isItemInBlend: (variantId: number | string) => boolean,
  updateBlendTemplate: (
    templateId: number,
    name: ?string,
    observation: ?string,
    complete: ?boolean,
    fatPercentage: ?number,
    fatLevel: ?('low' | 'medium' | 'high'),
  ) => void,
  createBlendItem: () => void,
  loading: boolean,
  addingBlendTemplateItemsLoading: boolean,
  blendNameTemplates: [],
  AddItemToBlendTemplate: (variantId: number, quantity: number) => void,
};

const UNUSED_FAT_PERCENTAGE = undefined;

const generateInitialBlendName = (blendTemplate, blendNameTemplates) => {
  if (blendTemplate && blendTemplate.name) {
    return blendTemplate.name;
  }

  let i = 0;
  while (true) {
    let newName = `Meu Blend ${i > 0 ? '(' + i + ')' : ''}`;
    const nameExists = blendNameTemplates.includes(newName);
    if (!nameExists) return newName;
    i++;
  }
};

const BlendCalculatorPage = ({
  blendTemplate,
  blendTemplateItems,
  isItemInBlend,
  updateBlendTemplate,
  createBlendItem,
  loading,
  addingBlendTemplateItemsLoading,
  blendNameTemplates,
  AddItemToBlendTemplate,
}: Props) => {
  const [name, setName] = useState<string>(() =>
    generateInitialBlendName(blendTemplate, blendNameTemplates),
  );

  const [observation, setObservation] = useState(
    () => blendTemplate?.observation ?? '',
  );

  const [fatLevel, setFatLevel] = useState<'low' | 'medium' | 'high'>(
    blendTemplate && blendTemplate.fatLevel ? blendTemplate.fatLevel : 'medium',
  );

  const [nameError, setNameError] = useState<?string>('');
  const [observationError, setObservationError] = useState<?string>('');
  const [blendItemsError, setBlendItemsError] = useState<?string>('');
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  const handleUpdateTemplate = (
    isComplete: boolean = false,
    nextFatLevel?: 'low' | 'medium' | 'high',
  ) => {
    if (blendTemplate && blendTemplate.id) {
      updateBlendTemplate(
        blendTemplate.id,
        name,
        observation,
        isComplete,
        UNUSED_FAT_PERCENTAGE,
        nextFatLevel !== undefined ? nextFatLevel : fatLevel,
      );
    }
  };

  const validateBlendInput = () => {
    let noValidationErrors = true;

    const nameErr = validationHelpers.blend.name(name);
    setNameError(nameErr);
    if (nameErr) {
      noValidationErrors = false;
    }

    const observationErr = validationHelpers.blend.observation(observation);
    setObservationError(observationErr);
    if (observationErr) {
      noValidationErrors = false;
    }

    const blendItemsErr =
      validationHelpers.blend.blendItems(blendTemplateItems);
    setBlendItemsError(blendItemsErr);
    if (blendItemsErr) {
      noValidationErrors = false;
    }

    if (noValidationErrors) {
      setIsConfirmationModalOpen(true);
    } else {
      console.error('Validation error on blend form.');
      scroller.scrollTo('blend-calculator-page-container', {
        duration: 1500,
        delay: 100,
        smooth: 'easeInOutQuart',
      });
    }
  };

  const handleConfirmSave = () => {
    setIsConfirmationModalOpen(false);
    handleUpdateTemplate(true);
  };

  const handleCloseModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const totalQuantityOfBlendTemplateItems = blendTemplateItems.reduce(
    (accumulated, current) => current.quantity + accumulated,
    0,
  );

  return (
    <div className="blend-calculator-page-main">
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        text="Pronto para salvar? Não será possível alterar seu Blend depois."
        onConfirm={handleConfirmSave}
        onClose={handleCloseModal}
      />

      <div className="blend-calculator-page-container">
        <div className="blend-calculator-page-content">
          <h3>CRIAR NOVO BLEND</h3>
          <div className="blend-calculator-page-header">
            <div className="blend-header-card">
              <div className="blend-image-wrapper">
                <img
                  className="blend-box-image"
                  src={BlendBox}
                  alt="Caixa de Carnes do Blend"
                />
              </div>

              <div className="blend-header-inputs">
                <div className="blend-header-top-row">
                  <div className="input-group blend-name-group">
                    <InputFieldErrorMessage message={nameError} />
                    <div className="input-wrapper">
                      <i
                        className="fa fa-tag input-icon"
                        aria-hidden="true"
                      ></i>
                      <input
                        className="blend-input-text blend-name"
                        placeholder="Dê um nome ao seu Blend..."
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onBlur={() => handleUpdateTemplate()}
                      />
                    </div>
                  </div>

                  <div className="blend-fat-section">
                    <FatLevelSelector
                      fatLevel={fatLevel}
                      updateFatLevel={(level: 'low' | 'medium' | 'high') => {
                        setFatLevel(level);
                        handleUpdateTemplate(false, level);
                      }}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <InputFieldErrorMessage message={observationError} />
                  <div className="input-wrapper textarea-wrapper">
                    <i
                      className="fa fa-comment-o input-icon"
                      aria-hidden="true"
                    ></i>
                    <textarea
                      rows="2"
                      className="blend-input-text blend-description"
                      placeholder="Ex: Preferência para hambúrguer na grelha, cortes mais limpos..."
                      value={observation}
                      onChange={e => setObservation(e.target.value)}
                      onBlur={() => handleUpdateTemplate()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="blend-calculator-page-subcontrainer">
            <ProductSearch
              blendTemplateId={blendTemplate ? blendTemplate.id : ''}
              isItemInBlend={isItemInBlend}
              AddItemToBlendTemplate={AddItemToBlendTemplate}
              addingBlendTemplateItemsLoading={addingBlendTemplateItemsLoading}
            />

            <div className="blend-calculator-page-right">
              <InputFieldErrorMessage message={blendItemsError} />
              <div className="blend-calculator-new-blend">
                <div className="blend-calculator-new-blend-header">
                  <h3>MEU BLEND</h3>
                </div>
                <div className="blend-calculator-new-blend-products">
                  {_.isEmpty(blendTemplateItems) ? (
                    <div className="blend-calculator-no-items">
                      <img src={EmptyCart} alt="" />
                      <span>
                        Sem itens. Adicione o seu primeiro item no painel
                        anterior :D
                      </span>
                    </div>
                  ) : (
                    blendTemplateItems.map(item => (
                      <BlendTemplateItem
                        key={item.id}
                        item={item}
                        blendTemplateId={blendTemplate ? blendTemplate.id : ''}
                        totalBlendQuantity={totalQuantityOfBlendTemplateItems}
                      />
                    ))
                  )}
                </div>
                <hr className="separator-line" />
                <div className="blend-calculator-total-info">
                  <p>{`Total: ${
                    blendTemplate ? blendTemplate.defaultQuantity / 1000 : 0
                  }Kg`}</p>
                  <p>
                    R$/Kg:{' '}
                    <span className="blend-calculator-total-info-price">
                      {priceConverter(
                        blendTemplate ? blendTemplate.price * 1000 : 0,
                      )}
                      /Kg
                    </span>
                  </p>
                  <p>Subtotal estimado:</p>
                  <p className="blend-calculator-total-info-amout">
                    {priceConverter(
                      blendTemplate && blendTemplate.amount
                        ? blendTemplate.amount
                        : 0,
                    )}
                  </p>
                </div>
              </div>
              <div className="blend-calculator-cart-button">
                <Button
                  text="SALVAR BLEND"
                  buttonType="button-secondary"
                  icon=""
                  onClick={() => {
                    validateBlendInput();
                    Analytics.logSavedBlend(blendTemplate);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    blendNameTemplates: selectors.blendTemplateItemsNamesSelector(state),
  };
};

const connector = connect(mapStateToProps);

export default connector(BlendCalculatorPage);
